import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Printer, BookOpen, NotebookPen, DoorOpen, PenLine } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

const H = "/api/htt";
const TITLES = {
  1: "When Someone Tells You Something",
  2: "Why Nothing Changed After the Training",
  3: "Making Meaning of What You See",
  4: "The Tools You Were Given",
  5: "Closing the Loop in Your Team",
  6: "Your Evidence, Not Your Attendance",
  7: "Being in the Room",
  8: "Your Practice Picture",
};
const MOVEMENT = { 1: "Perceive", 2: "Perceive", 3: "Perceive", 4: "Intervene", 5: "Intervene", 6: "Redesign", 7: "Redesign", 8: "Redesign" };

function parsePage(page) {
  const m = (page || "").match(/m(\d)/);
  const n = m ? Number(m[1]) : 0;
  let type = "Worksheet", dir = "modules/";
  if (/expanded-workbook/.test(page)) { type = "Workbook"; dir = "modules/"; }
  else if (/-practice-room/.test(page) || /complete-course/.test(page)) { type = "Practice room"; dir = "rooms/"; }
  return { n, type, dir };
}

function answersFrom(store) {
  return Object.entries(store || {})
    .filter(([k, v]) => typeof v === "string" && v.trim().length > 0)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, v]) => v.trim());
}

export default function Practice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/reflections")
      .then(({ data }) => {
        const byModule = {};
        (data.reflections || []).forEach((r) => {
          const { n, type, dir } = parsePage(r.page);
          if (!n) return;
          const ans = answersFrom(r.store);
          if (!ans.length) return;
          if (!byModule[n]) byModule[n] = { n, entries: [] };
          byModule[n].entries.push({ type, dir, page: r.page, answers: ans, updated_at: r.updated_at });
        });
        setModules(Object.values(byModule).sort((a, b) => a.n - b.n));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const total = modules.reduce((s, m) => s + m.entries.reduce((x, e) => x + e.answers.length, 0), 0);

  return (
    <div className="min-h-screen bg-wash">
      <SiteNav variant="dark" />

      <section className="pt-[96px] pb-10 bg-purple-deep velvet no-print">
        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8">
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-3">Your reflections, gathered</p>
          <h1 className="font-heading text-white text-[clamp(2rem,5vw,3.2rem)] leading-tight">
            {user?.name ? `${user.name}'s practice` : "My practice"}
          </h1>
          <p className="mt-3 text-white/75 max-w-xl">
            Everything you've written across the course, held in one private place. It's yours — revisit it, add to it, or print it to keep.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-5 py-2.5 border border-white/25 text-white hover:bg-white/10 transition-colors" data-testid="practice-back-btn">
              <ArrowLeft className="w-4 h-4" /> My course
            </button>
            {total > 0 && (
              <button onClick={() => window.print()} className="inline-flex items-center gap-2 text-sm font-bold rounded-full px-5 py-2.5 bg-gold text-purple-deep hover:bg-gold-pale transition-colors" data-testid="practice-print-btn">
                <Printer className="w-4 h-4" /> Print my practice
              </button>
            )}
          </div>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <div className="hidden print:block mb-8">
          <h1 className="font-heading text-purple text-3xl">My Practice — How Truth Travels</h1>
          <p className="text-lav text-sm">{user?.name} · Oxytocin-Learning</p>
        </div>

        {loading ? (
          <p className="text-lav font-heading italic text-xl">Gathering your words…</p>
        ) : total === 0 ? (
          <div className="bg-white rounded-2xl border border-line p-10 text-center" data-testid="practice-empty">
            <PenLine className="w-8 h-8 text-lav mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-purple mb-2">Nothing written yet</h2>
            <p className="text-lav mb-6">As you type into the worksheets, your reflections gather here automatically.</p>
            <Link to="/dashboard" className="inline-flex items-center gap-2 bg-purple-deep text-cream font-bold rounded-full px-6 py-3">
              Start a module
            </Link>
          </div>
        ) : (
          <div className="space-y-8" data-testid="practice-list">
            {modules.map((m, mi) => (
              <motion.section
                key={m.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: mi * 0.04 }}
                className="bg-white rounded-2xl border border-line overflow-hidden break-inside-avoid"
                data-testid={`practice-module-${m.n}`}
              >
                <div className="px-7 py-5 border-b border-line bg-cream flex items-baseline justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold">{MOVEMENT[m.n]} · Module {m.n}</p>
                    <h2 className="font-heading text-2xl text-purple leading-tight">{TITLES[m.n]}</h2>
                  </div>
                </div>
                <div className="p-7 space-y-6">
                  {m.entries.map((e, ei) => (
                    <div key={ei}>
                      <p className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] text-lav mb-3">
                        {e.type === "Workbook" ? <NotebookPen className="w-3.5 h-3.5" /> : e.type === "Practice room" ? <DoorOpen className="w-3.5 h-3.5" /> : <BookOpen className="w-3.5 h-3.5" />} {e.type}
                      </p>
                      <ul className="space-y-3">
                        {e.answers.map((a, ai) => (
                          <li key={ai} className="border-l-2 border-gold pl-4 text-ink/85 leading-relaxed italic">
                            “{a}”
                          </li>
                        ))}
                      </ul>
                      <a href={`${H}/${e.dir}${e.page}`} className="no-print inline-block mt-4 text-[13px] font-semibold text-purple-mid hover:text-purple underline underline-offset-4">
                        Open this {e.type.toLowerCase()} →
                      </a>
                    </div>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>
        )}
      </main>

      <div className="no-print"><SiteFooter /></div>
    </div>
  );
}
